import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Plus, Trash2, Eye, Pencil, AlertTriangle, CheckCircle2, XCircle, MoreHorizontal } from "lucide-react";
import Modal from "../components/Modal";
import SearchableSelect from "../components/SearchableSelect";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { FormError } from "../components/ui/feedback";
import {
  DataValue,
  PassiveBadge,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableRefreshingRow,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";
import {
  DateRangeSelector,
  TopLevelFilterArea,
  TopLevelFilterClear,
  TopLevelFilterField,
  TopLevelFilterRow,
  TopLevelFilterSearch,
  TopLevelSelectFilter,
  TopLevelTextFilter,
  hasTopLevelFilterConstraints,
  matchesDateRange,
  useSearchFilterState,
} from "../components/ui/search-filter";


function ModelCell({ item }) {
  return <DataValue value={item?.model} className={!item ? "text-zinc-400" : undefined} />;
}

function ItemCell({ item, partName }) {
  return (
    <div>
      <div>{partName}</div>
      {(item?.compatible_models || []).length > 0 && (
        <div className="text-[10px] text-zinc-400">{item.compatible_models.join(", ")}</div>
      )}
    </div>
  );
}

const STATUS_BADGE_TONE = {
  draft: "neutral",
  pending: "warning",
  approved: "success",
  received: "info",
  rejected: "danger",
  cancelled: "danger",
};

function normalizeTransferDraft({ destination_warehouse_id, notes, lines }) {
  return {
    destination_warehouse_id: String(destination_warehouse_id || ""),
    notes: String(notes || ""),
    lines: (Array.isArray(lines) ? lines : [])
      .filter((line) => line?.item_id && Number(line.quantity) > 0)
      .map((line) => ({ item_id: String(line.item_id), quantity: Number(line.quantity) }))
      .sort((left, right) => (
        left.item_id.localeCompare(right.item_id) || left.quantity - right.quantity
      )),
  };
}

const EMPTY_TRANSFER_DRAFT = Object.freeze({
  destination_warehouse_id: "",
  notes: "",
  lines: [],
});

export default function Transfer() {
  const { warehouses, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";

  const [items, setItems]     = useState([]);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");
  const [modalError, setModalError] = useState("");

  // Admin direct transfer form
  const [src, setSrc]   = useState("");
  const [dst, setDst]   = useState("");
  const [lines, setLines] = useState([{ model: "", item_id: "", quantity: 1 }]);
  const [notes, setNotes] = useState("");

  // Recent Transfers filters + pagination
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(10);
  const historyFilters = useSearchFilterState({
    initialState: {
      topLevelDraft: { source: "", destination: "", by: "", status: "" },
      topLevelApplied: { source: "", destination: "", by: "", status: "" },
    },
    dateRangeMode: "draft",
    onPageReset: () => setHistPage(1),
  });
  const historyDraft = historyFilters.state.topLevelDraft;
  const historyApplied = historyFilters.state.topLevelApplied;
  const historyDateDraft = historyFilters.state.dateRangeDraft;
  const historyDateApplied = historyFilters.state.dateRangeApplied;
  const hasHistoryConstraints = hasTopLevelFilterConstraints(historyFilters.state);
  const setHistoryDraft = (name, value) => historyFilters.setTopLevelDraft((current) => ({ ...current, [name]: value }));

  // Modals
  const [confirmOpen, setConfirmOpen]           = useState(false);
  const [pendingTransfer, setPendingTransfer]   = useState(null);
  const [detail, setDetail]                     = useState(null);
  const [requestDetail, setRequestDetail]       = useState(null);
  const [proceedTarget, setProceedTarget]       = useState(null);
  const [proceedLines, setProceedLines]         = useState([]);
  const [editingApproved, setEditingApproved]   = useState(false);
  const [receiveTarget, setReceiveTarget]       = useState(null);
  const [cancelTarget, setCancelTarget]         = useState(null);
  const [rejectTarget, setRejectTarget]         = useState(null);
  const [rejectReason, setRejectReason]         = useState("");

  // Staff draft form
  const [editing, setEditing]                   = useState(false);
  const [draftDst, setDraftDst]                 = useState("");
  const [draftLines, setDraftLines]             = useState([{ model: "", item_id: "", quantity: 1 }]);
  const [draftNotes, setDraftNotes]             = useState("");
  const [clearModalOpen, setClearModalOpen]     = useState(false);
  const [submitPreviewOpen, setSubmitPreviewOpen] = useState(false);
  const draftInitRef = useRef(null);
  const draftBaselineRef = useRef(EMPTY_TRANSFER_DRAFT);

  const load = async () => {
    setListLoading(true);
    setListError("");
    try {
      const [i, h, r] = await Promise.all([
        api.get("/items"),
        api.get("/transfers"),
        api.get("/transfer-requests"),
      ]);
      setItems(i.data);
      setHistory(h.data);
      setRequests(r.data);
    } catch (requestError) {
      setListError(formatApiError(requestError));
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (listLoading) return;
    const requestId = searchParams.get("view_request");
    const transferId = searchParams.get("view_transfer");
    if (requestId) {
      const selectedRequest = requests.find((request) => request.id === requestId);
      if (selectedRequest) setRequestDetail(selectedRequest);
    } else if (transferId) {
      const selectedTransfer = history.find((transfer) => transfer.id === transferId);
      if (selectedTransfer) setDetail(selectedTransfer);
    }
  }, [history, listLoading, requests, searchParams]);
  const clearRequestViewQuery = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("view_request");
    setSearchParams(next, { replace: true });
  };
  const clearTransferViewQuery = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("view_transfer");
    setSearchParams(next, { replace: true });
  };

  const mainWarehouse     = warehouses.find((w) => w.code === "MAIN");
  const nonMainWarehouses = warehouses.filter((w) => w.code !== "MAIN");
  const MAIN_WAREHOUSE_ID = warehouses?.find(w => w.code === "MAIN")?.id ?? null;
  const mainEligibleItems = useMemo(() => (
    items.filter((item) => Number(item.stock?.[MAIN_WAREHOUSE_ID] || 0) > 0)
  ), [items, MAIN_WAREHOUSE_ID]);
  const mainEligibleModels = useMemo(() => (
    Array.from(new Set(mainEligibleItems.map((item) => item.model).filter(Boolean)))
      .sort().map((model) => ({ value: model, label: model }))
  ), [mainEligibleItems]);

  const myDraft   = useMemo(() => requests.find((r) => r.status === "draft"),                          [requests]);
  const allPending = useMemo(() => requests.filter((r) => r.status === "pending"),                     [requests]);
  const myPending  = useMemo(() => allPending.filter((r) => r.created_by === user?.id),                 [allPending, user]);
  const myAwaitingReceipt = useMemo(() => requests.filter((r) => (
    r.status === "approved" && r.receipt_required === true && r.created_by === user?.id
  )), [requests, user]);
  const activeRequests = useMemo(() => requests.filter((r) => (
    r.status === "pending" || (r.status === "approved" && r.receipt_required === true)
  )), [requests]);
  const submitBlocked = myPending.length > 0 || myPending.length + myAwaitingReceipt.length >= 2;
  const submitWarning = myPending.length > 0
    ? "Another request is awaiting Admin approval. Receive or resolve it before submitting a new request."
    : myPending.length + myAwaitingReceipt.length >= 2
      ? "Two requests are awaiting receipt. Receive one before submitting another request."
      : null;

  // Sync draft form state when server draft changes (new draft / draft deleted)
  useEffect(() => {
    const id = myDraft?.id ?? null;
    if (id === draftInitRef.current) return;
    draftInitRef.current = id;
    if (myDraft) {
      setDraftDst(myDraft.destination_warehouse_id || "");
      setDraftNotes(myDraft.notes || "");
      setDraftLines(
        myDraft.lines.length > 0
          ? myDraft.lines.map((l) => ({
              model: items.find((i) => i.id === l.item_id)?.model || "",
              item_id: l.item_id,
              quantity: l.quantity,
            }))
          : [{ model: "", item_id: "", quantity: 1 }]
      );
      draftBaselineRef.current = normalizeTransferDraft(myDraft);
    } else {
      setDraftDst("");
      setDraftNotes("");
      setDraftLines([{ model: "", item_id: "", quantity: 1 }]);
      draftBaselineRef.current = EMPTY_TRANSFER_DRAFT;
    }
    setEditing(false);
  }, [myDraft, items]);

  // ── Admin direct transfer ──────────────────────────────────
  const addLine    = () => setLines([...lines, { model: "", item_id: "", quantity: 1 }]);
  const updateLine = (i, p) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...p } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = () => {
    setFormError("");
    if (!src || !dst) return setFormError("Select both warehouses");
    if (src === dst) return setFormError("Source and destination must differ");
    const valid = lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (valid.length === 0) return setFormError("Add at least one item");
    const enriched = valid.map((l) => {
      const item = items.find((i) => i.id === l.item_id);
      return { item_id: l.item_id, quantity: Number(l.quantity), part_name: item?.part_name || "—", part_number: item?.part_number || "—" };
    });
    setPendingTransfer({ src, dst, lines: enriched, notes });
    setModalError("");
    setConfirmOpen(true);
  };

  const executeTransfer = async () => {
    setLoading(true);
    setModalError("");
    try {
      await api.post("/transfers", {
        source_warehouse_id: pendingTransfer.src,
        destination_warehouse_id: pendingTransfer.dst,
        lines: pendingTransfer.lines.map((l) => ({ item_id: l.item_id, quantity: l.quantity })),
        notes: pendingTransfer.notes,
      });
      toast.success("Transfer completed");
      setConfirmOpen(false);
      setPendingTransfer(null);
      setLines([{ model: "", item_id: "", quantity: 1 }]);
      setNotes("");
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Staff draft operations ─────────────────────────────────
  const addDraftLine    = () => setDraftLines([...draftLines, { model: "", item_id: "", quantity: 1 }]);
  const updateDraftLine = (i, p) => setDraftLines(draftLines.map((l, idx) => idx === i ? { ...l, ...p } : l));
  const removeDraftLine = (i) => setDraftLines(draftLines.filter((_, idx) => idx !== i));

  const draftDirty = editing && JSON.stringify(normalizeTransferDraft({
    destination_warehouse_id: draftDst,
    notes: draftNotes,
    lines: draftLines,
  })) !== JSON.stringify(draftBaselineRef.current);
  const draftGuard = useUnsavedChangesGuard(draftDirty && !loading);

  const cancelEdit = () => {
    if (myDraft) {
      setDraftDst(myDraft.destination_warehouse_id || "");
      setDraftNotes(myDraft.notes || "");
      setDraftLines(
        myDraft.lines.length > 0
          ? myDraft.lines.map((l) => ({
              model: items.find((i) => i.id === l.item_id)?.model || "",
              item_id: l.item_id,
              quantity: l.quantity,
            }))
          : [{ model: "", item_id: "", quantity: 1 }]
      );
    } else {
      setDraftDst("");
      setDraftNotes("");
      setDraftLines([{ model: "", item_id: "", quantity: 1 }]);
    }
    setEditing(false);
  };

  const requestCancelEdit = () => draftGuard.requestLeave(cancelEdit);

  const saveDraft = async () => {
    setFormError("");
    if (!draftDst) return setFormError("Select a destination warehouse");
    const valid = draftLines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (valid.length === 0) return setFormError("Add at least one item");
    setLoading(true);
    try {
      const payload = {
        destination_warehouse_id: draftDst,
        lines: valid.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) })),
        notes: draftNotes,
        ...(myDraft ? { expected_updated_at: myDraft.updated_at } : {}),
      };
      const { data: savedDraft } = await api.post("/transfer-requests", payload);
      setRequests((current) => {
        const withoutSavedDraft = current.filter((request) => request.id !== savedDraft.id);
        return [savedDraft, ...withoutSavedDraft];
      });
      draftBaselineRef.current = normalizeTransferDraft(savedDraft);
      toast.success(myDraft ? "Draft updated" : "Draft saved");
      setEditing(false);
    } catch (e) {
      setFormError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const clearDraft = async () => {
    if (!myDraft) return;
    setLoading(true);
    setModalError("");
    try {
      await api.delete(`/transfer-requests/${myDraft.id}`, {
        data: { expected_updated_at: myDraft.updated_at },
      });
      toast.success("Draft cleared");
      setClearModalOpen(false);
      setEditing(false);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const confirmSubmit = async () => {
    setLoading(true);
    setModalError("");
    try {
      await api.post(`/transfer-requests/${myDraft.id}/submit`);
      toast.success("Request submitted for approval");
      setSubmitPreviewOpen(false);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Admin approve/reject ───────────────────────────────────
  const openApprove = (req, edit = false) => {
    setModalError("");
    setProceedTarget(req);
    setEditingApproved(edit);
    if (edit) {
      const approvedByItem = new Map((req.approved_lines || []).map((line) => [line.item_id, line]));
      const originalByItem = new Map();
      (req.lines || []).forEach((line) => {
        const current = originalByItem.get(line.item_id);
        originalByItem.set(line.item_id, {
          ...line,
          requested_quantity: (current?.requested_quantity || 0) + Number(line.quantity || 0),
          quantity: 0,
          model: items.find((item) => item.id === line.item_id)?.model || "",
          isAdded: false,
        });
      });
      (req.approved_lines || []).forEach((line) => {
        const original = originalByItem.get(line.item_id);
        originalByItem.set(line.item_id, {
          ...line,
          requested_quantity: original?.requested_quantity ?? Number(line.requested_quantity || 0),
          model: items.find((item) => item.id === line.item_id)?.model || "",
          isAdded: !original && Number(line.requested_quantity || 0) === 0,
        });
      });
      setProceedLines(Array.from(originalByItem.values()));
      return;
    }
    const requested = new Map();
    req.lines.forEach((line) => {
      const current = requested.get(line.item_id);
      requested.set(line.item_id, {
        ...line,
        requested_quantity: (current?.requested_quantity || 0) + Number(line.quantity || 0),
        quantity: (current?.requested_quantity || 0) + Number(line.quantity || 0),
        model: items.find((item) => item.id === line.item_id)?.model || "",
        isAdded: false,
      });
    });
    setProceedLines(Array.from(requested.values()));
  };

  const updateProceedLine = (i, p) => setProceedLines(proceedLines.map((l, idx) => idx === i ? { ...l, ...p } : l));
  const addProceedLine = () => setProceedLines([...proceedLines, { model: "", item_id: "", requested_quantity: 0, quantity: 1, isAdded: true }]);
  const removeProceedLine = (i) => updateProceedLine(i, { quantity: 0 });

  const executeApprove = async () => {
    const valid = proceedLines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (valid.length === 0) return setModalError("At least one item required");
    setLoading(true);
    setModalError("");
    try {
      const payload = { lines: valid.map((l) => ({ item_id: l.item_id, quantity: Number(l.quantity) })) };
      if (editingApproved) {
        payload.expected_updated_at = proceedTarget.updated_at;
        await api.patch(`/transfer-requests/${proceedTarget.id}/approved`, payload);
      } else {
        await api.post(`/transfer-requests/${proceedTarget.id}/approve`, payload);
      }
      toast.success(editingApproved ? "Approved request updated" : "Request approved and awaiting receipt");
      setProceedTarget(null);
      setEditingApproved(false);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const executeReceive = async () => {
    if (!receiveTarget) return;
    setLoading(true);
    setModalError("");
    try {
      await api.post(`/transfer-requests/${receiveTarget.id}/receive`, {
        expected_updated_at: receiveTarget.updated_at,
      });
      toast.success("Transfer received");
      setReceiveTarget(null);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const executeCancelRequest = async () => {
    if (!cancelTarget) return;
    setLoading(true);
    setModalError("");
    try {
      await api.post(`/transfer-requests/${cancelTarget.id}/cancel`, {
        expected_updated_at: cancelTarget.updated_at,
      });
      toast.success("Request cancelled");
      setCancelTarget(null);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const executeReject = async () => {
    setLoading(true);
    setModalError("");
    try {
      await api.post(`/transfer-requests/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Request rejected");
      setRejectTarget(null);
      load();
    } catch (e) {
      setModalError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────
  const whName  = (id) => warehouses.find((w) => w.id === id)?.name || id;
  const stockAt = (itemId, whId) => items.find((i) => i.id === itemId)?.stock?.[whId] ?? 0;

  const allModels = useMemo(() => {
    const set = new Set(items.map((i) => i.model).filter(Boolean));
    return Array.from(set).sort().map((m) => ({ value: m, label: m }));
  }, [items]);

  // Combined history: completed transfers + terminal request outcomes
  const combinedHistory = useMemo(() => {
    const terminalReqs = requests.filter((r) => ["rejected", "cancelled"].includes(r.status)).map((r) => ({
      ...r,
      _kind: `${r.status}_request`,
      type: r.status,
      created_at: r.resolved_at || r.updated_at,
      source_warehouse_id: MAIN_WAREHOUSE_ID,
    }));
    const transfers = history.map((t) => ({ ...t, _kind: "transfer" }));
    return [...transfers, ...terminalReqs].sort((a, b) => {
      const byCreatedAt = new Date(b.created_at) - new Date(a.created_at);
      if (byCreatedAt !== 0) return byCreatedAt;
      return String(b.id || "").localeCompare(String(a.id || ""));
    });
  }, [history, requests, MAIN_WAREHOUSE_ID]);

  const filteredHistory = useMemo(() => {
    return combinedHistory.filter((t) => {
      const historyStatus = transferHistoryStatus(t);
      if (historyApplied.source && t.source_warehouse_id !== historyApplied.source) return false;
      if (historyApplied.destination && t.destination_warehouse_id !== historyApplied.destination) return false;
      if (historyApplied.status && historyStatus !== historyApplied.status) return false;
      if ((historyDateApplied.from || historyDateApplied.to) && !matchesDateRange(String(t.created_at || "").slice(0, 10), historyDateApplied)) return false;
      if (historyApplied.by && !(t.created_by_email || "").toLowerCase().includes(historyApplied.by.toLowerCase())) return false;
      return true;
    });
  }, [combinedHistory, historyApplied.source, historyApplied.destination, historyApplied.by, historyApplied.status, historyDateApplied]);

  const histTotalPages = Math.ceil(filteredHistory.length / histPageSize);
  const histPageItems  = filteredHistory.slice((histPage - 1) * histPageSize, histPage * histPageSize);
  const requestComparison = requestDetail ? buildRequestComparison(requestDetail, items) : [];
  const detailRequest = detail?.request_id ? requests.find((request) => request.id === detail.request_id) : null;
  useEffect(() => {
    const lastPage = Math.max(histTotalPages, 1);
    if (histPage > lastPage) setHistPage(lastPage);
  }, [histPage, histTotalPages]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <OrdinaryPageBody data-testid="transfer-page">
      <OrdinaryPageHeader title="Transfer" actions={<RefreshButton onClick={load} loading={listLoading} accessibleLabel="Refresh transfers" data-testid="transfer-refresh" />} data-testid="transfer-page-header" />
      <FormError>{formError}</FormError>

      {/* ── ADMIN: Create direct transfer ── */}
      {isAdmin && (
        <div className="bg-white border border-zinc-200 rounded-sm" data-testid="transfer-create-card">
          <div className="border-b border-zinc-200 px-6 py-4">
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">New transfer order</div>
            <div className="text-base font-semibold tracking-tight">Create Transfer</div>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
              <WhSelect label="Source warehouse" value={src} onChange={setSrc} warehouses={warehouses} testid="transfer-source-select" exclude={dst} />
              <ArrowRight className="hidden md:block w-5 h-5 text-zinc-400 mb-3" />
              <WhSelect label="Destination warehouse" value={dst} onChange={setDst} warehouses={warehouses} testid="transfer-destination-select" exclude={src} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">Items</div>
              <div className="space-y-2" data-testid="transfer-lines">
                {lines.map((l, idx) => {
                  const available = src && l.item_id ? stockAt(l.item_id, src) : null;
                  const overQty   = available !== null && Number(l.quantity) > available;
                  const itemOptions = (l.model 
                    ? items.filter((i) => 
                        i.model === l.model || 
                        (i.compatible_models && i.compatible_models.includes(l.model))
                      ) 
                    : items).map((i) => ({
                    value: i.id, 
                    label: i.part_name,
                    sublabel: `${i.part_number} · ${i.model}${i.compatible_models?.includes(l.model) && i.model !== l.model ? ` (compatible with ${l.model})` : ""}`,
                  }));
                  return (
                    <div key={idx} className="grid grid-cols-[1fr_1.5fr_110px_40px] gap-2 items-start" data-testid={`transfer-line-${idx}`}>
                      <SearchableSelect value={l.model} onChange={(v) => updateLine(idx, { model: v, item_id: "" })} options={allModels} placeholder="Model…" testid={`transfer-line-model-${idx}`} allowClear />
                      <SearchableSelect value={l.item_id} onChange={(v) => updateLine(idx, { item_id: v })} options={itemOptions} placeholder={l.model ? `Item from ${l.model}…` : "Select item…"} testid={`transfer-line-item-${idx}`} />
                      <div>
                        <input type="number" min="1" value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} className={`wms-input ${overQty ? "border-destructive" : ""}`} data-testid={`transfer-line-qty-${idx}`} />
                        {available !== null && <div className={`text-[10px] mt-1 font-medium tabular-nums ${overQty ? "text-destructive" : "text-zinc-500"}`}>avail: {available}</div>}
                      </div>
                      <Button type="button" onClick={() => removeLine(idx)} disabled={lines.length === 1} variant="transfer-remove" size="row-icon" data-action-category="transfer-remove" title="Remove item" aria-label={`Remove transfer item ${idx + 1}`} data-testid={`transfer-line-remove-${idx}`}>
                        <Trash2 strokeWidth={2} />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <Button type="button" onClick={addLine} variant="transfer-add" size="sm" className="mt-3" data-action-category="transfer-add" data-testid="transfer-add-line">
                <Plus strokeWidth={2} /> Add item
              </Button>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Notes</div>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional reference note" className="wms-input" data-testid="transfer-notes-input" />
            </div>
            <div className="flex justify-end pt-3 border-t border-zinc-200">
              <Button type="button" onClick={submit} loading={loading} loadingLabel="Processing…" variant="success" data-action-category="submit-confirm" data-testid="transfer-submit-btn">
                Submit transfer<ArrowRight strokeWidth={2} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADMIN: Pending requests approval queue ── */}
      {false && isAdmin && (
        <ListTableShell>
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">Approval queue</div>
              <div className="text-base font-semibold tracking-tight">Pending Requests</div>
            </div>
            <div className="text-xs font-medium tabular-nums text-zinc-500">{activeRequests.length} active</div>
          </div>
          <ListTableViewport>
          <ListTable data-testid="pending-requests-table">
            <thead>
              <tr>
                <th style={{width:"200px"}}>Submitted</th>
                <th style={{width:"180px"}}>Staff</th>
                <th style={{width:"220px"}}>To</th>
                <th style={{width:"100px"}}>Items</th>
                <th>Notes</th>
                <th style={{textAlign:"right", width:"100px"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              <ListTableRefreshingRow active={listLoading && allPending.length > 0} colSpan={6} />
              {listLoading && allPending.length === 0 ? (
                <ListTableState colSpan={6} kind="loading">Loading pending requests...</ListTableState>
              ) : listError ? (
                <ListTableState colSpan={6} kind="error" onRetry={load}>{listError}</ListTableState>
              ) : allPending.length === 0 ? (
                <ListTableState colSpan={6}>No pending requests.</ListTableState>
              ) : allPending.map((req) => (
                <tr key={req.id} data-testid={`request-row-${req.id}`}>
                  <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatPresentationTimestamp(req.submitted_at || req.created_at)}</td>
                  <td className="max-w-[180px] text-xs"><DataValue value={req.created_by_email} containment="truncate" /></td>
                  <td className="text-xs font-semibold">{whName(req.destination_warehouse_id)}</td>
                  <td className="text-xs">{(req.status === "approved" ? (req.approved_lines || []) : req.lines).length} item{(req.status === "approved" ? (req.approved_lines || []) : req.lines).length !== 1 ? "s" : ""}</td>
                  <td className="max-w-[240px] text-xs text-zinc-500"><DataValue value={req.notes} /></td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <Button type="button" onClick={() => openApprove(req)} variant="success" size="row-icon" title="Approve" aria-label={`Approve request from ${req.created_by_email}`} data-action-category="submit-confirm" data-testid={`request-approve-${req.id}`}>
                        <CheckCircle2 strokeWidth={2} />
                      </Button>
                      <Button type="button" onClick={() => { setModalError(""); setRejectTarget(req); setRejectReason(""); }} variant="transfer-remove" size="row-icon" title="Reject" aria-label={`Reject request from ${req.created_by_email}`} data-action-category="destructive" data-testid={`request-reject-${req.id}`}>
                        <XCircle strokeWidth={2} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
          </ListTableViewport>
        </ListTableShell>
      )}

      {/* ── STAFF: Draft card ── */}
      {!isAdmin && (
        <div className="bg-white border border-zinc-200 rounded-sm" data-testid="draft-card">
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">Main Warehouse → {draftDst ? whName(draftDst) : "…"}</div>
            <div className="text-base font-semibold tracking-tight">
                {editing ? (myDraft ? "Edit Draft" : "New Request") : (myDraft ? "Transfer Request" : "Transfer Request")}
              </div>
            </div>
            {myDraft && !editing && (
              <PassiveBadge tone="neutral" className="uppercase tracking-wide">Draft</PassiveBadge>
            )}
          </div>

          {/* View mode — no draft */}
          {!editing && !myDraft && (
            <div className="p-8 flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-zinc-500">No draft saved. Create a transfer request from Main Warehouse to a field warehouse.</p>
              <Button type="button" onClick={() => setEditing(true)} variant="default" data-action-category="create-add" data-testid="draft-create-btn">
                Create Draft
              </Button>
            </div>
          )}

          {/* View mode — draft exists */}
          {!editing && myDraft && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Destination</div>
                  <div className="font-semibold text-sm">{whName(myDraft.destination_warehouse_id)}</div>
                </div>
                {myDraft.notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Notes</div>
                    <div className="text-sm text-zinc-600">{myDraft.notes}</div>
                  </div>
                )}
              </div>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <table className="wms-table">
                  <thead>
                    <tr>
                      <th style={{width:"130px"}}>Part #</th>
                      <th style={{width:"170px"}}>Model</th>
                      <th>Item</th>
                      <th style={{width:"70px"}} className="num whitespace-nowrap">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myDraft.lines.map((l, i) => {
                      const fullItem = items.find((it) => it.id === l.item_id);
                      return (
                        <tr key={i}>
                          <td className="text-sm font-semibold tracking-[0.02em]">{l.part_number}</td>
                          <td className="text-sm"><ModelCell item={fullItem} /></td>
                          <td className="text-sm"><ItemCell item={fullItem} partName={l.part_name} /></td>
                          <td className="num font-semibold">{l.quantity}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {submitWarning && (
                <div data-testid="draft-submit-warning" className="flex items-start gap-2 bg-semantic-warning border border-warning/30 px-3 py-2.5 rounded-sm text-xs text-foreground font-medium">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  {submitWarning}
                </div>
              )}
              <div className="flex justify-between pt-3">
                <Button type="button" onClick={() => setEditing(true)} variant="edit" data-action-category="edit" data-testid="draft-edit-btn">
                  Edit Draft
                </Button>
                <Button
                  type="button"
                  onClick={() => { setModalError(""); setSubmitPreviewOpen(true); }}
                  disabled={submitBlocked}
                  title={submitBlocked ? "Resolve an outstanding request first" : ""}
                  variant="success"
                  data-action-category="review-preview"
                  data-testid="draft-submit-btn"
                >
                  Submit for Approval <ArrowRight strokeWidth={2} />
                </Button>
              </div>
            </div>
          )}

          {/* Edit mode */}
          {editing && (
            <div className="p-5 space-y-5">
              <div>
                <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Destination Warehouse</div>
                <select value={draftDst} onChange={(e) => setDraftDst(e.target.value)} className="wms-input" data-testid="draft-dst-select">
                  <option value="">Select destination…</option>
                  {nonMainWarehouses.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
                </select>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">Items</div>
                <div className="space-y-2">
                  {draftLines.map((l, idx) => {
                    const available = mainWarehouse && l.item_id ? stockAt(l.item_id, mainWarehouse.id) : null;
                    const overQty   = available !== null && Number(l.quantity) > available;
                    const itemOptions = (l.model 
                      ? mainEligibleItems.filter((i) =>
                          i.model === l.model || 
                          (i.compatible_models && i.compatible_models.includes(l.model))
                        ) 
                      : mainEligibleItems).map((i) => ({
                      value: i.id, 
                      label: i.part_name,
                      sublabel: `${i.part_number} · ${i.model}${i.compatible_models?.includes(l.model) && i.model !== l.model ? ` (compatible with ${l.model})` : ""}`,
                    }));
                    return (
                      <div key={idx} className="grid grid-cols-[1fr_1.5fr_110px_40px] gap-2 items-start">
                        <SearchableSelect value={l.model} onChange={(v) => updateDraftLine(idx, { model: v, item_id: "" })} options={mainEligibleModels} placeholder="Model…" allowClear />
                        <SearchableSelect value={l.item_id} onChange={(v) => updateDraftLine(idx, { item_id: v })} options={itemOptions} placeholder={l.model ? `Item from ${l.model}…` : "Select item…"} />
                        <div>
                          <input type="number" min="1" value={l.quantity} onChange={(e) => updateDraftLine(idx, { quantity: e.target.value })} className={`wms-input ${overQty ? "border-destructive" : ""}`} />
                          {available !== null && <div className={`text-[10px] mt-1 font-medium tabular-nums ${overQty ? "text-destructive" : "text-zinc-500"}`}>avail: {available}</div>}
                        </div>
                        <Button type="button" onClick={() => removeDraftLine(idx)} disabled={draftLines.length === 1} variant="transfer-remove" size="row-icon" title="Remove item" data-action-category="transfer-remove" aria-label={`Remove draft item ${idx + 1}`}>
                          <Trash2 strokeWidth={2} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <Button type="button" onClick={addDraftLine} variant="transfer-add" size="sm" className="mt-3" data-action-category="transfer-add">
                  <Plus strokeWidth={2} /> Add item
                </Button>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Notes</div>
                <input value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} placeholder="Optional reference note" className="wms-input" />
              </div>

              <div className="flex justify-between gap-2 pt-3 border-t border-zinc-200">
                <Button
                  type="button"
                  onClick={() => { setModalError(""); setClearModalOpen(true); }}
                  disabled={!myDraft}
                  variant="destructive"
                  data-action-category="cancel"
                  data-testid="draft-clear-btn"
                >
                  Clear
                </Button>
                <div className="flex gap-2">
                  <Button type="button" onClick={requestCancelEdit} variant="cancel" data-action-category="cancel" data-testid="draft-cancel-btn">
                    Cancel
                  </Button>
                  <Button type="button" onClick={saveDraft} loading={loading} loadingLabel="Saving…" variant="success" data-action-category="submit-confirm" data-testid="draft-save-btn">
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STAFF: Request Part — all pending (view-only) ── */}
      {(
        <ListTableShell>
          <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">Part Requests</div>
            <div className="text-base font-semibold tracking-tight">Request Part</div>
            </div>
            <div className="text-xs font-medium tabular-nums text-zinc-500">{allPending.length} pending</div>
          </div>
          <ListTableViewport>
          <ListTable data-testid="request-part-table">
            <thead>
              <tr>
                <th style={{width:"200px"}}>Submitted</th>
                <th style={{width:"180px"}}>Staff</th>
                <th style={{width:"220px"}}>To</th>
                <th style={{width:"70px"}} className="num whitespace-nowrap">Items</th>
                <th style={{width:"80px"}} className="num whitespace-nowrap">Qty</th>
                <th style={{width:"120px"}}>Status</th>
                <th>Notes</th>
                <th style={{textAlign:"right", width:"130px"}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              <ListTableRefreshingRow active={listLoading && activeRequests.length > 0} colSpan={8} />
              {listLoading && activeRequests.length === 0 ? (
                <ListTableState colSpan={8} kind="loading">Loading pending requests...</ListTableState>
              ) : listError ? (
                <ListTableState colSpan={8} kind="error" onRetry={load}>{listError}</ListTableState>
              ) : activeRequests.length === 0 ? (
                <ListTableState colSpan={8}>No active requests.</ListTableState>
              ) : activeRequests.map((req) => (
                <tr key={req.id} data-testid={`request-part-row-${req.id}`} className={req.created_by === user?.id ? "bg-accent/30" : undefined}>
                  <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatPresentationTimestamp(req.submitted_at || req.created_at)}</td>
                  <td className="max-w-[180px] text-xs"><DataValue value={req.created_by_email} containment="truncate" /></td>
                  <td className="text-xs font-semibold">{whName(req.destination_warehouse_id)}</td>
                  <td className="num">{req.lines.length}</td>
                  <td className="num font-semibold">{requestQuantity(req)}</td>
                  <td><PassiveBadge tone={req.status === "approved" ? "info" : STATUS_BADGE_TONE[req.status] || "neutral"}>{(req.status || "pending").toUpperCase()}</PassiveBadge></td>
                  <td className="max-w-[240px] text-xs text-zinc-500"><DataValue value={req.notes} /></td>
                  <td>
                    <div className="flex items-center justify-start gap-2">
                      <Button type="button" onClick={() => setRequestDetail(req)} variant="info" size="row-icon" title="View request" aria-label={`View request from ${req.created_by_email}`} data-action-category="view-info" data-testid={`request-part-view-${req.id}`}><Eye strokeWidth={2} /></Button>
                      {req.status === "pending" && isAdmin && <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button type="button" variant="neutral" size="row-icon" title="More actions" aria-label="More request actions" data-action-category="utility"><MoreHorizontal /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => openApprove(req)} data-testid={`request-approve-${req.id}`}><CheckCircle2 />Approve</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => { setModalError(""); setRejectTarget(req); setRejectReason(""); }} variant="destructive" data-action-category="destructive" data-testid={`request-reject-${req.id}`}><XCircle />Reject</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>}
                      {req.status === "pending" && !isAdmin && req.created_by === user?.id && <Button type="button" onClick={() => { setModalError(""); setCancelTarget(req); }} variant="cancel" size="row-icon" title="Cancel request" aria-label="Cancel request" data-action-category="cancel" data-testid={`request-cancel-${req.id}`}><XCircle strokeWidth={2} /></Button>}
                      {req.status === "approved" && isAdmin && <Button type="button" onClick={() => openApprove(req, true)} variant="edit" size="row-icon" title="Edit approved request" aria-label="Edit approved request" data-action-category="edit" data-testid={`request-edit-approved-${req.id}`}><Pencil strokeWidth={2} /></Button>}
                      {req.status === "approved" && !isAdmin && req.created_by === user?.id && <Button type="button" onClick={() => { setModalError(""); setReceiveTarget(req); }} variant="success" size="row-icon" title="Receive" aria-label="Receive request" data-action-category="submit-confirm" data-testid={`request-receive-${req.id}`}><ArrowRight strokeWidth={2} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
          </ListTableViewport>
        </ListTableShell>
      )}

      {/* ── Recent Transfers history ── */}
      <ListTableShell>
        <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">History</div>
            <div className="text-base font-semibold tracking-tight">Recent Transfers</div>
          </div>
        </div>

        <TopLevelFilterArea className="rounded-none border-x-0 border-t-0 bg-zinc-50/50 px-5 py-3" data-testid="transfer-history-filters">
          <TopLevelFilterRow className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(9rem,1fr)_minmax(9rem,1fr)_minmax(10rem,1.2fr)_minmax(8rem,0.8fr)_minmax(10rem,1fr)_auto]">
            <TopLevelFilterField label="Source" htmlFor="transfer-filter-source">
              <TopLevelSelectFilter id="transfer-filter-source" value={historyDraft.source || ""} onChange={(event) => setHistoryDraft("source", event.target.value)} data-testid="transfer-filter-source">
                <option value="">All sources</option>
                {warehouses.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
              </TopLevelSelectFilter>
            </TopLevelFilterField>
            <TopLevelFilterField label="Destination" htmlFor="transfer-filter-destination">
              <TopLevelSelectFilter id="transfer-filter-destination" value={historyDraft.destination || ""} onChange={(event) => setHistoryDraft("destination", event.target.value)} data-testid="transfer-filter-destination">
                <option value="">All destinations</option>
                {warehouses.map((w) => (<option key={w.id} value={w.id}>{w.name}</option>))}
              </TopLevelSelectFilter>
            </TopLevelFilterField>
            <TopLevelFilterField label="By" htmlFor="transfer-filter-by">
              <TopLevelTextFilter id="transfer-filter-by" value={historyDraft.by || ""} onChange={(event) => setHistoryDraft("by", event.target.value)} placeholder="Email…" data-testid="transfer-filter-by" />
            </TopLevelFilterField>
            <TopLevelFilterField label="Status" htmlFor="transfer-filter-status">
              <TopLevelSelectFilter id="transfer-filter-status" value={historyDraft.status || ""} onChange={(event) => setHistoryDraft("status", event.target.value)} data-testid="transfer-filter-status">
                <option value="">All</option>
                <option value="direct">Direct</option>
                <option value="received">Received</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </TopLevelSelectFilter>
            </TopLevelFilterField>
            <DateRangeSelector mode="draft" value={historyDateDraft} onChange={historyFilters.setDateRangeDraft} id="transfer-filter-date" data-testid="transfer-filter-date" />
            <div className="flex items-center justify-end gap-1 self-end">
              <TopLevelFilterClear onClick={historyFilters.clearTopLevelFilters} disabled={!hasHistoryConstraints} data-action-category="utility" data-testid="transfer-filter-clear">Clear All</TopLevelFilterClear>
              <TopLevelFilterSearch onClick={historyFilters.applyTopLevelFilters} data-testid="transfer-filter-search" />
            </div>
          </TopLevelFilterRow>
        </TopLevelFilterArea>

        <ListTableViewport>
        <ListTable data-testid="transfer-history">
          <thead>
            <tr>
              <th style={{width:"220px"}}>Date</th>
              <th>Route</th>
                <th style={{width:"70px"}} className="num whitespace-nowrap">Items</th>
                <th style={{width:"90px"}} className="num whitespace-nowrap">Total Qty</th>
              <th style={{textAlign:"right", width:"150px"}}>Status</th>
              <th style={{width:"170px"}}>By</th>
              <th style={{width:"150px", textAlign:"right"}}>Details</th>
            </tr>
          </thead>
          <tbody>
            <ListTableRefreshingRow active={listLoading && histPageItems.length > 0} colSpan={7} />
            {listLoading && histPageItems.length === 0 ? (
              <ListTableState colSpan={7} kind="loading">Loading transfer history...</ListTableState>
            ) : listError ? (
              <ListTableState colSpan={7} kind="error" onRetry={load}>{listError}</ListTableState>
            ) : histPageItems.length === 0 ? (
              <ListTableState colSpan={7}>No transfers match.</ListTableState>
            ) : histPageItems.map((t) => (
              <tr key={t.id} data-testid={`transfer-history-row-${t.id}`}>
                <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatPresentationTimestamp(t.created_at)}</td>
                <td>
                  <div className="text-xs flex items-center gap-2">
                    <span className="font-semibold">{whName(t.source_warehouse_id)}</span>
                    <ArrowRight className="w-3 h-3 text-zinc-400" />
                    <span className="font-semibold">{whName(t.destination_warehouse_id)}</span>
                  </div>
                </td>
                <td className="num text-xs">{(t.lines || []).length}</td>
                <td className="num">{(t.lines || []).reduce((a, l) => a + l.quantity, 0)}</td>
                <td style={{textAlign:"right"}}>
                  <PassiveBadge tone={transferHistoryStatus(t) === "received" ? "success" : transferHistoryStatus(t) === "direct" ? "info" : ["rejected", "cancelled"].includes(t.type) ? "danger" : "neutral"}>
                    {transferHistoryStatus(t) === "received" ? "RECEIVED" : transferHistoryStatus(t) === "direct" ? "DIRECT" : (t.type || "direct").toUpperCase()}
                  </PassiveBadge>
                </td>
                <td className="text-xs">{t.created_by_email}</td>
                <td className="text-right">
                  <Button
                    type="button"
                    onClick={() => t._kind === "rejected_request" ? setRequestDetail(t) : setDetail(t)}
                    data-testid={`transfer-view-detail-${t.id}`}
                    variant="info"
                    size="row-icon"
                    className="ml-auto"
                    title="View details"
                    aria-label={`View transfer details ${t.id}`}
                    data-action-category="view-info"
                  >
                    <Eye strokeWidth={2} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </ListTable>
        </ListTableViewport>

        <ListPagination
          page={histPage}
          pageSize={histPageSize}
          total={filteredHistory.length}
          totalPages={histTotalPages}
          loading={listLoading}
          onPrevious={() => setHistPage((current) => Math.max(1, current - 1))}
          onNext={() => setHistPage((current) => Math.min(histTotalPages, current + 1))}
          onPageSizeChange={(nextPageSize) => { setHistPageSize(nextPageSize); setHistPage(1); }}
          pageSizeAriaLabel="Transfer history rows per page"
          summaryTestId="transfer-history-pagination-summary"
        />
      </ListTableShell>

      {/* ── ADMIN: Approve modal ── */}
      {receiveTarget && (
        <Modal title="Receive Transfer Request" onClose={() => setReceiveTarget(null)} busy={loading} testid="receive-modal" size="lg">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <div className="flex items-center gap-3 text-sm font-semibold">
              <span>{whName(MAIN_WAREHOUSE_ID)}</span><ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" /><span>{whName(receiveTarget.destination_warehouse_id)}</span>
            </div>
            <p className="text-sm text-zinc-600">Confirm receipt of the Admin-approved quantities. Quantities cannot be edited here.</p>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <RequestComparisonTable request={receiveTarget} items={items} testid="receive-approved-lines" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setReceiveTarget(null)} variant="cancel" data-action-category="cancel">Cancel</Button>
              <Button type="button" onClick={executeReceive} loading={loading} loadingLabel="Receiving…" variant="success" data-action-category="submit-confirm" data-testid="receive-confirm-btn">Confirm Receive<ArrowRight strokeWidth={2} /></Button>
            </div>
          </div>
        </Modal>
      )}

      {proceedTarget && (
        <Modal title={editingApproved ? "Edit Approved Transfer Request" : "Approve Transfer Request"} onClose={() => { setProceedTarget(null); setEditingApproved(false); }} busy={loading} testid="approve-modal" size="lg">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold text-zinc-500">{warehouses.find(w => w.id === proceedTarget.source_warehouse_id)?.name ?? warehouses.find(w => w.code === "MAIN")?.name}</span>
              <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="font-semibold">{whName(proceedTarget.destination_warehouse_id)}</span>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Requested by {proceedTarget.created_by_email}</div>
            <div className="border border-zinc-200 rounded-sm overflow-visible">
              <table className="wms-table">
                <thead>
                  <tr>
                    <th>Part No.</th>
                    <th>Model</th>
                    <th>Part</th>
                    <th style={{width:"90px"}} className="num whitespace-nowrap">Qty</th>
                    <th style={{width:"44px"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {proceedLines.map((l, idx) => {
                    const available = mainWarehouse && l.item_id ? stockAt(l.item_id, mainWarehouse.id) : 0;
                    const overQty   = l.item_id && Number(l.quantity) > available;
                    const otherSelectedIds = new Set(proceedLines.filter((line, lineIndex) => lineIndex !== idx).map((line) => line.item_id).filter(Boolean));
                    const eligibleOptions = mainEligibleItems.filter((item) => !otherSelectedIds.has(item.id) || item.id === l.item_id);
                    const itemOptions = (l.model 
                      ? eligibleOptions.filter((i) =>
                          i.model === l.model || 
                          (i.compatible_models && i.compatible_models.includes(l.model))
                        ) 
                      : eligibleOptions).map((i) => ({
                      value: i.id, label: i.part_name,
                      sublabel: `${i.part_number}${(i.compatible_models || []).length > 0 ? ` | ${i.compatible_models.join(", ")}` : ""}`,
                    }));
                    return (
                      <tr key={idx}>
                        <td className="text-xs text-zinc-500">{l.part_number || mainEligibleItems.find((item) => item.id === l.item_id)?.part_number || "—"}</td>
                        <td style={{width:"140px"}}>
                          <SearchableSelect
                            value={l.model}
                            onChange={(value) => updateProceedLine(idx, { model: value, item_id: "", requested_quantity: 0, isAdded: true })}
                            options={mainEligibleModels}
                            disabled={!l.isAdded}
                            placeholder="Model…"
                            allowClear
                          />
                        </td>
                        <td>
                          <SearchableSelect
                            value={l.item_id}
                            onChange={(value) => updateProceedLine(idx, { item_id: value, model: mainEligibleItems.find((item) => item.id === value)?.model || "", requested_quantity: 0, isAdded: true })}
                            options={itemOptions}
                            disabled={!l.isAdded}
                            placeholder="Select item…"
                          />
                        </td>
                        <td>
                          <div className="flex flex-col gap-0.5">
                            <input
                              type="number" min="0" max={available} value={l.quantity}
                              onChange={(e) => updateProceedLine(idx, { quantity: Number(e.target.value) })}
                              className={`wms-input ${overQty ? "border-destructive" : ""}`}
                            />
                            {l.item_id && <div className={`text-[10px] font-medium tabular-nums ${overQty ? "text-destructive" : "text-zinc-500"}`}>avail: {available}</div>}
                          </div>
                        </td>
                        <td>
                          <Button type="button" onClick={() => updateProceedLine(idx, { quantity: 0 })} variant="transfer-remove" size="row-icon" title="Omit item" data-action-category="transfer-remove" aria-label={`Omit approval item ${idx + 1}`}>
                            <Trash2 strokeWidth={2} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Button type="button" onClick={addProceedLine} variant="transfer-add" size="sm" data-action-category="transfer-add"><Plus strokeWidth={2} /> Add Part</Button>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setProceedTarget(null)} variant="cancel" data-action-category="cancel">Cancel</Button>
              <Button type="button" onClick={executeApprove} loading={loading} loadingLabel="Approving…" variant="success" data-action-category="submit-confirm" data-testid="approve-confirm-btn">
                {editingApproved ? "Save Approved State" : "Approve Request"}<ArrowRight strokeWidth={2} />
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── ADMIN: Reject modal ── */}
      {rejectTarget && (
        <Modal title="Reject Request" onClose={() => setRejectTarget(null)} busy={loading} testid="reject-modal">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <p className="text-sm text-zinc-700">
              Reject transfer request from <span className="font-semibold">{rejectTarget.created_by_email}</span> to <span className="font-semibold">{whName(rejectTarget.destination_warehouse_id)}</span>? No stock changes will be made.
            </p>
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Reason (optional)</div>
              <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="wms-input" placeholder="Reason for rejection…" data-testid="reject-reason-input" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setRejectTarget(null)} variant="navigation-back" data-action-category="navigation-back">Back</Button>
              <Button type="button" onClick={executeReject} loading={loading} loadingLabel="Rejecting…" variant="destructive" data-action-category="destructive" data-testid="reject-confirm-btn">
                Confirm Reject
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Confirm direct transfer modal ── */}
      {confirmOpen && pendingTransfer && (
        <Modal title="Confirm Transfer" onClose={() => setConfirmOpen(false)} busy={loading} testid="transfer-confirm-modal">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <div className="flex items-center gap-3 text-sm font-semibold">
              <span>{whName(pendingTransfer.src)}</span>
              <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
              <span>{whName(pendingTransfer.dst)}</span>
            </div>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <table className="wms-table">
                <thead><tr><th>Part No.</th><th>Model</th><th>Part</th><th className="num whitespace-nowrap">Qty</th></tr></thead>
                <tbody>
                  {pendingTransfer.lines.map((l, i) => {
                    const fullItem = items.find((it) => it.id === l.item_id);
                    return (
                      <tr key={i}>
                        <td className="text-xs text-zinc-500">{l.part_number || "—"}</td>
                        <td className="text-xs"><ModelCell item={fullItem} /></td>
                        <td className="text-xs"><ItemCell item={fullItem} partName={l.part_name} /></td>
                        <td className="num font-semibold">{l.quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pendingTransfer.notes && (
              <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2">{pendingTransfer.notes}</div>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setConfirmOpen(false)} variant="cancel" data-action-category="cancel">Cancel</Button>
              <Button type="button" onClick={executeTransfer} loading={loading} loadingLabel="Processing…" variant="success" data-action-category="submit-confirm" data-testid="transfer-confirm-btn">
                Confirm Transfer<ArrowRight strokeWidth={2} />
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Staff: Submit preview modal ── */}
      {submitPreviewOpen && myDraft && (
        <Modal title="Confirm Submit Request" onClose={() => setSubmitPreviewOpen(false)} busy={loading} testid="submit-preview-modal">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold text-zinc-500">{whName(MAIN_WAREHOUSE_ID)}</span>
              <ArrowRight className="w-4 h-4 text-zinc-400 shrink-0" />
              <span className="font-semibold">{whName(myDraft.destination_warehouse_id)}</span>
            </div>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <table className="wms-table">
                <thead><tr><th>Part No.</th><th>Model</th><th>Part</th><th className="num whitespace-nowrap">Requested</th></tr></thead>
                <tbody>
                  {myDraft.lines.map((l, i) => {
                    const fullItem = items.find((it) => it.id === l.item_id);
                    return (
                      <tr key={i}>
                        <td className="text-xs text-zinc-500">{l.part_number || "—"}</td>
                        <td className="text-xs"><ModelCell item={fullItem} /></td>
                        <td className="text-xs"><ItemCell item={fullItem} partName={l.part_name} /></td>
                        <td className="num font-semibold">{l.quantity}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50">
                    <td colSpan={3} className="font-semibold text-xs uppercase tracking-wider text-zinc-600">Total</td>
                      <td className="num font-medium">{myDraft.lines.reduce((a, l) => a + l.quantity, 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {myDraft.notes && (
              <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2">{myDraft.notes}</div>
            )}
            <p className="text-xs text-zinc-500">Once submitted, your request will be sent to admin for approval. You cannot edit it until it is resolved.</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setSubmitPreviewOpen(false)} variant="cancel" data-action-category="cancel">Cancel</Button>
              <Button type="button" onClick={confirmSubmit} loading={loading} loadingLabel="Submitting…" variant="success" data-action-category="submit-confirm" data-testid="submit-preview-confirm-btn">
                Submit for Approval<ArrowRight strokeWidth={2} />
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Staff: Clear draft confirmation modal ── */}
      {clearModalOpen && (
        <Modal title="Clear Draft" onClose={() => setClearModalOpen(false)} busy={loading} testid="clear-draft-modal">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <p className="text-sm text-zinc-700">Are you sure you want to clear this draft? All items and selections will be permanently deleted.</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setClearModalOpen(false)} variant="cancel" data-action-category="cancel">Cancel</Button>
              <Button type="button" onClick={clearDraft} loading={loading} loadingLabel="Clearing…" variant="destructive" data-action-category="destructive" data-testid="clear-draft-confirm-btn">
                Clear Draft
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {cancelTarget && (
        <Modal title="Cancel Transfer Request" onClose={() => setCancelTarget(null)} busy={loading} testid="cancel-request-modal">
          <div className="space-y-4">
            <FormError>{modalError}</FormError>
            <p className="text-sm text-zinc-700">Cancel your pending request to <span className="font-semibold">{whName(cancelTarget.destination_warehouse_id)}</span>? No stock changes will be made.</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setCancelTarget(null)} variant="cancel" data-action-category="cancel">Back</Button>
              <Button type="button" onClick={executeCancelRequest} loading={loading} loadingLabel="Cancelling…" variant="destructive" data-action-category="destructive" data-testid="cancel-request-confirm-btn">Confirm Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
      {draftGuard.dialog}

      {/* ── Transfer request detail modal (pending view / rejected history) ── */}
      {requestDetail && (
        <Modal title="Transfer Request" onClose={() => { setRequestDetail(null); clearRequestViewQuery(); }} testid="request-detail-modal" size="lg">
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <DetailCell label="Requested by"><div className="text-xs">{requestDetail.created_by_email}</div></DetailCell>
              <DetailCell label="Destination"><div className="font-semibold text-sm">{whName(requestDetail.destination_warehouse_id)}</div></DetailCell>
              <DetailCell label="Status">
                <PassiveBadge tone={STATUS_BADGE_TONE[requestDetail.status] || "neutral"} data-testid="transfer-request-status">
                  {(requestDetail.status || "pending").toUpperCase()}
                </PassiveBadge>
              </DetailCell>
              {requestDetail.submitted_at && (
                <DetailCell label="Submitted"><div className="text-xs font-medium tabular-nums">{formatPresentationTimestamp(requestDetail.submitted_at)}</div></DetailCell>
              )}
              {requestDetail.resolved_at && (
                <DetailCell label="Resolved"><div className="text-xs font-medium tabular-nums">{formatPresentationTimestamp(requestDetail.resolved_at)}</div></DetailCell>
              )}
            </div>
            {requestDetail.reject_reason && (
              <div className="bg-semantic-danger border border-destructive/30 px-3 py-2.5 rounded-sm">
                <div className="text-[10px] uppercase tracking-wider text-semantic-danger font-semibold mb-1">Reject Reason</div>
                <div className="text-sm text-semantic-danger">{requestDetail.reject_reason}</div>
              </div>
            )}
            {requestDetail.notes && (
              <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 rounded-sm">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Notes</div>
                <div className="text-sm">{requestDetail.notes}</div>
              </div>
            )}
            {!(requestDetail.status === "approved" && requestDetail.receipt_required === true) && <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">Items · {(requestDetail.lines || []).length}</div>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <table className="wms-table">
                  <thead>
                    <tr>
                          <th style={{width:"130px"}}>Part No.</th>
                          <th style={{width:"110px"}}>Model</th>
                          <th>Part</th>
                          <th style={{width:"80px"}} className="num whitespace-nowrap">Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(requestDetail.lines || []).map((l, i) => {
                      const fullItem = items.find((it) => it.id === l.item_id);
                      return (
                        <tr key={i}>
                          <td className="text-xs text-zinc-500">{l.part_number || "—"}</td>
                          <td className="text-xs"><ModelCell item={fullItem} /></td>
                          <td className="text-sm"><ItemCell item={fullItem} partName={l.part_name} /></td>
                          <td className="num font-semibold">{l.quantity}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50">
                      <td colSpan={3} className="font-semibold text-xs uppercase tracking-wider text-zinc-600">Total</td>
                      <td className="num font-medium">{(requestDetail.lines || []).reduce((a, l) => a + l.quantity, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>}
            {requestDetail.status === "approved" && requestDetail.receipt_required === true && (
              <div>
                <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">Requested vs Approved</div>
                <div className="border border-zinc-200 rounded-sm overflow-hidden">
                  <table className="wms-table" data-testid="request-comparison-table">
                    <thead><tr><th>Part No.</th><th>Model</th><th>Part</th><th className="num whitespace-nowrap">Requested</th><th className="num whitespace-nowrap">Approved</th><th>Change</th></tr></thead>
                    <tbody>{requestComparison.map((line) => (
                      <tr key={line.item_id}>
                        <td className="text-xs text-zinc-500">{line.part_number || "—"}</td>
                        <td className="text-xs text-zinc-500">{line.model || "—"}</td>
                        <td className="text-sm">{line.part_name}</td>
                        <td className="num">{line.requested}</td>
                        <td className="num">{line.approved}</td>
                        <td className="text-xs font-medium">{line.requested === 0 ? "Added by Admin" : line.approved === 0 ? "Not approved" : `${line.approved - line.requested > 0 ? "+" : ""}${line.approved - line.requested}`}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── Transfer detail modal (completed transfers) ── */}
      {detail && (
        <Modal title={`Transfer · ${detail.id.slice(0, 8)}`} onClose={() => { setDetail(null); clearTransferViewQuery(); }} testid="transfer-detail-modal" size="lg">
          <div className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DetailCell label="Date"><div className="text-xs font-medium tabular-nums">{formatPresentationTimestamp(detail.created_at)}</div></DetailCell>
              <DetailCell label="Source"><div className="font-semibold text-sm">{whName(detail.source_warehouse_id)}</div></DetailCell>
              <DetailCell label="Destination"><div className="font-semibold text-sm">{whName(detail.destination_warehouse_id)}</div></DetailCell>
              <DetailCell label="Created by"><div className="text-xs">{detail.created_by_email}</div></DetailCell>
            </div>
            {detail.notes && (
              <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 rounded-sm">
                <div className="text-[10px] tracking-wider uppercase text-zinc-500 font-semibold mb-1">Notes</div>
                <div className="text-sm">{detail.notes}</div>
              </div>
            )}
            {detailRequest ? (
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <RequestComparisonTable request={detailRequest} items={items} testid="transfer-request-comparison-table" />
              </div>
            ) : <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-2">Line items · {detail.lines.length}</div>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <table className="wms-table">
                  <thead>
                    <tr>
                      <th style={{width:"130px"}}>Part No.</th>
                      <th style={{width:"110px"}}>Model</th>
                      <th>Part</th>
                      <th style={{width:"80px"}} className="num whitespace-nowrap">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((l, i) => {
                      const fullItem = items.find((it) => it.id === l.item_id);
                      return (
                        <tr key={l.item_id + i}>
                          <td className="text-xs text-zinc-500">{l.part_number || "—"}</td>
                          <td className="text-xs"><ModelCell item={fullItem} /></td>
                          <td className="text-sm"><ItemCell item={fullItem} partName={l.part_name} /></td>
                          <td className="num font-semibold">{l.quantity}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50">
                      <td colSpan={3} className="font-semibold text-xs uppercase tracking-wider text-zinc-600">Total</td>
                      <td className="num font-medium">{detail.lines.reduce((a, l) => a + l.quantity, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>}
          </div>
        </Modal>
      )}
    </OrdinaryPageBody>
  );
}

function DetailCell({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.1em] uppercase text-zinc-500 font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}

function requestQuantity(request) {
  const lines = request?.status === "approved" && request?.receipt_required === true
    ? request?.approved_lines || []
    : request?.lines || [];
  return lines.reduce((total, line) => total + Number(line.quantity || 0), 0);
}

function transferHistoryStatus(transfer) {
  if (transfer?._kind === "rejected_request") return "rejected";
  if (transfer?._kind === "cancelled_request") return "cancelled";
  if (transfer?._kind !== "transfer") return "";
  return transfer.request_id ? "received" : "direct";
}

function buildRequestComparison(request, items = []) {
  const rows = new Map();
  (request?.lines || []).forEach((line) => {
    rows.set(line.item_id, {
      item_id: line.item_id,
      part_name: line.part_name,
      part_number: line.part_number,
      model: items.find((item) => item.id === line.item_id)?.model || line.model || "",
      requested: Number(line.quantity || 0),
      approved: 0,
    });
  });
  (request?.approved_lines || []).forEach((line) => {
    const current = rows.get(line.item_id);
    rows.set(line.item_id, {
      item_id: line.item_id,
      part_name: line.part_name,
      part_number: line.part_number,
      model: items.find((item) => item.id === line.item_id)?.model || line.model || "",
      requested: current?.requested ?? Number(line.requested_quantity || 0),
      approved: Number(line.quantity || 0),
    });
  });
  return Array.from(rows.values()).sort((left, right) => left.item_id.localeCompare(right.item_id));
}

function RequestComparisonTable({ request, items, testid }) {
  return (
    <table className="wms-table" data-testid={testid}>
      <thead><tr><th>Part No.</th><th>Model</th><th>Part</th><th className="num whitespace-nowrap">Requested</th><th className="num whitespace-nowrap">Approved</th><th>Change</th></tr></thead>
      <tbody>{buildRequestComparison(request, items).map((line) => (
        <tr key={line.item_id}>
          <td className="text-xs text-zinc-500">{line.part_number || "—"}</td>
          <td className="text-xs text-zinc-500">{line.model || "—"}</td>
          <td className="text-sm">{line.part_name}</td>
          <td className="num">{line.requested}</td>
          <td className="num">{line.approved}</td>
          <td className="text-xs font-medium">{line.requested === 0 ? "Added by Admin" : line.approved === 0 ? "Not approved" : `${line.approved - line.requested > 0 ? "+" : ""}${line.approved - line.requested}`}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

function WhSelect({ label, value, onChange, warehouses, testid, exclude }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="wms-input" data-testid={testid}>
        <option value="">Select…</option>
        {warehouses.filter((w) => w.id !== exclude).map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
      </select>
    </div>
  );
}
